<script setup lang="ts">
/* 
  Basic collapsable to have custom elements inside with slots
  1. Showed on header is part of the content to be expanded keepHeaderStatic: false
  2. Header is a fixed content separate from content keepHeaderStatic: true
  3. Invert arrows invertArrows: true
*/
const props = withDefaults(
  defineProps<{
    keepHeaderStatic?: boolean
    invertArrows?: boolean
    showCollapse?: boolean
    alwaysOpen?: boolean
  }>(),
  {
    keepHeaderStatic: false,
    invertArrows: false,
    showCollapse: undefined,
    alwaysOpen: false,
  }
)
const emit = defineEmits(['show-change'])

const isOpen = ref(false)

const collapseIcon = computed(() => {
  const value = props.invertArrows ? !isOpen.value : isOpen.value
  if (value) return '/images/icons/arrowUp.svg'
  else return '/images/icons/arrowDown.svg'
})

const changeVisibility = () => {
  if (props.alwaysOpen) return
  isOpen.value = !isOpen.value
  emit('show-change', isOpen.value)
}

onMounted(() => {
  if (props.alwaysOpen) isOpen.value = true
})
</script>

<template>
  <div>
    <div
      data-test="mcd-collapsable__element__visible"
      class="flex flex-row items-center justify-between"
      :class="{ 'cursor-pointer': !props.alwaysOpen }"
      @click="changeVisibility"
    >
      <!-- Showed on header is part of the content that will be expanded -->
      <template v-if="!keepHeaderStatic">
        <div class="flex-1">
          <Transition name="mix-content">
            <template v-if="isOpen">
              <slot name="content"></slot>
            </template>
            <template v-else>
              <slot name="header"></slot>
            </template>
          </Transition>
        </div>
      </template>

      <!-- Header is always fixed -->
      <template v-else>
        <slot name="header"></slot>
      </template>
      <img
        v-if="showCollapse === undefined || showCollapse"
        class="mx-2"
        :class="{ 'mt-2 self-start': !keepHeaderStatic && isOpen }"
        :src="collapseIcon"
        alt="collapsable"
      />
    </div>
    <Transition name="slide-in" appear>
      <section data-test="mcd-collapsable__element__content">
        <div v-show="keepHeaderStatic && isOpen" class="mt-2">
          <slot name="content"></slot>
        </div>
      </section>
    </Transition>
  </div>
</template>
